import React from 'react'
import { useTranslation } from 'react-i18next'
import { StreamData } from 'types/streamData'
import AuthorSex, { AuthorSexStrings } from 'enums/AuthorSex'
import SoundbiteSentiment, { SoundbiteSentimentStrings } from 'enums/SoundbiteSentiment'
import FemaleIcon from './FemaleIcon'
import MaleIcon from './MaleIcon'
import MapMarkerIcon from './MapMarkerIcon'
import PersonIcon from './PersonIcon'
import SentimentMixedIcon from './SentimentMixedIcon'
import SentimentNegastiveIcon from './SentimentNegativeIcon'
import SentimentNeutralIcon from './SentimentNeutralIcon'
import SentimentPositiveIcon from './SentimentPositiveIcon'
import SourceIcon from './SourceIcon'
import UnknownGenderIcon from './UnknownGenderIcon'
import KarmaIcon from './KarmaIcon'
import ShoppingCartIcon from './ShoppingCartIcon'
import TagIcon from './TagIcon'
import DomainIcon from '../DomainIcon/DomainIcon'

type GenderIconProps = {
  gender?: AuthorSexStrings
}

const GenderIcon = ({ gender }: GenderIconProps) => {
  switch (gender) {
    case AuthorSex.MALE:
      return <MaleIcon />
    case AuthorSex.FEMALE:
      return <FemaleIcon />
    default:
      return <UnknownGenderIcon />
  }
}

type GeoLocationIconProps = {
  geoLocation: string[]
  geoLocationGenealogy: string[]
}

const GeoLocationIcon = ({ geoLocation, geoLocationGenealogy }: GeoLocationIconProps) => {
  const { t } = useTranslation()
  return (
    <>
      {geoLocation.map((location, index) => (
        <MapMarkerIcon
          message={location}
          tooltip={t('authorsGeo', { geo: geoLocationGenealogy[index] })}
          // eslint-disable-next-line react/no-array-index-key
          key={index}
        />
      ))}
    </>
  )
}

const hasAuthorFollower = (authorFolower?: string | number) => {
  if (authorFolower === undefined) {
    return false
  }
  if (typeof authorFolower === 'number') {
    return authorFolower !== 0
  }
  return true
}

type SentimentIconProps = {
  sentiment: SoundbiteSentimentStrings
  annotatedSentiment?: SoundbiteSentimentStrings
}

const SentimentIcon = ({ sentiment, annotatedSentiment }: SentimentIconProps) => {
  const annotated = annotatedSentiment !== undefined

  switch (sentiment) {
    case SoundbiteSentiment.POSITIVE:
      return <SentimentPositiveIcon annotated={annotated} />
    case SoundbiteSentiment.NEGATIVE:
      return <SentimentNegastiveIcon annotated={annotated} />
    case SoundbiteSentiment.MIXED:
      return <SentimentMixedIcon />
    case SoundbiteSentiment.NEUTRAL:
    default:
      return <SentimentNeutralIcon annotated={annotated} />
  }
}

type SourceDomainIconProps = {
  streamData: StreamData
}

const SourceDomainIcon = ({ streamData }: SourceDomainIconProps) => (
  <SourceIcon message={streamData.domain!} url={streamData.url!}>
    <DomainIcon streamData={streamData} />
  </SourceIcon>
)

type MetadataIconsProps = {
  streamData: StreamData
  showSentenceTags?: boolean
}

const MetadataIcons = ({ streamData, showSentenceTags = true }: MetadataIconsProps) => {
  const {
    authorGender,
    authorGeoHigh,
    authorGeoHighGenealogy,
    authorFollower,
    authorKarma,
    author,
    authorName,
    productName,
    productNameText,
    productHierarchy,
    soundbiteSentiment,
    documentMetaTags,
    sentenceMetaTags,
    sentenceUserTags,
    annotatedSentiment,
    url
  } = streamData
  const { t } = useTranslation()

  const uniqueSentenceMetaTags = Array.from(new Set(sentenceMetaTags))

  return (
    <>
      {(author || authorName) && <GenderIcon gender={authorGender} />}
      {authorGeoHigh && authorGeoHighGenealogy && (
        <GeoLocationIcon
          geoLocation={authorGeoHigh}
          geoLocationGenealogy={authorGeoHighGenealogy}
        />
      )}
      {authorKarma && <KarmaIcon message={`${authorKarma}`} />}
      {hasAuthorFollower(authorFollower) && <PersonIcon message={`${authorFollower}`} />}
      {productNameText && (
        <ShoppingCartIcon
          productNameText={productNameText}
          productName={productName}
          productHierarchy={productHierarchy}
        />
      )}
      {soundbiteSentiment && (
        <SentimentIcon sentiment={soundbiteSentiment} annotatedSentiment={annotatedSentiment} />
      )}
      {documentMetaTags &&
        documentMetaTags.map((tag) => (
          <TagIcon message={tag} tooltipPrefix={`${t('postLevelTag')}:`} key={tag} />
        ))}
      {showSentenceTags &&
        uniqueSentenceMetaTags &&
        uniqueSentenceMetaTags.map((tag) => (
          <TagIcon message={tag} tooltipPrefix={`${t('mentionLevelTag')}:`} key={tag} />
        ))}
      {showSentenceTags &&
        sentenceUserTags &&
        sentenceUserTags.map((tag) => (
          <TagIcon message={tag} tooltipPrefix={`${t('mentionLevelTag')}:`} key={tag} />
        ))}
      {url && <SourceDomainIcon streamData={streamData} />}
    </>
  )
}

export default MetadataIcons
